import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useChineseLang } from '../../i18n/chinese'
import ZhWords, { ClickWordHandler } from 'components/write-speak/ZhText'
import {
  blockAuthor,
  deleteReply,
  Discussion,
  DiscussionAnswer,
  DiscussionReply,
  DiscussionTargetKind,
  errorMessage,
  likeItem,
  moderateItem,
  postReply,
  reportItem,
} from './discussionApi'
import { replyTime } from './discussionLogic'
import { Avatar, LikeButton, MenuAction, MoreMenu, TeacherTick } from './DiscussionBits'

type Key = string
const keyOf = (kind: DiscussionTargetKind, id: string): Key => `${kind}:${id}`

/**
 * State shared by the side panel and the discussion page: which thread is open, the one reply
 * box (with a draft kept per answer), and what reporting, blocking and moderating do.
 */
export const useDiscussionThreads = (
  discussion: Discussion | null,
  setDiscussion: (update: (d: Discussion) => Discussion) => void,
  onReload: () => void,
  focusRef: string | null,
) => {
  const { t } = useTranslation('discussion')
  const [openRef, setOpenRef] = useState<string | null>(focusRef)
  const [replyTo, setReplyTo] = useState<DiscussionAnswer | null>(null)
  const [draft, setDraft] = useState('')
  const drafts = useRef(new Map<string, string>())
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reported, setReported] = useState<Set<Key>>(new Set())
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    if (focusRef) setOpenRef(focusRef)
  }, [focusRef])

  const keepDraft = () => {
    if (replyTo) drafts.current.set(replyTo.ref, draft)
  }

  const startReply = (answer: DiscussionAnswer) => {
    keepDraft()
    setReplyTo(answer)
    setDraft(drafts.current.get(answer.ref) || '')
    setOpenRef(answer.ref)
    setError(null)
  }

  const cancelReply = () => {
    keepDraft()
    setReplyTo(null)
    setError(null)
  }

  const send = async () => {
    if (!replyTo || sending) return
    const ref = replyTo.ref
    setSending(true)
    setError(null)
    try {
      const reply = await postReply(ref, draft)
      setDiscussion((d) => ({
        ...d,
        answers: d.answers.map((a) => (a.ref === ref ? { ...a, replies: [...a.replies, reply] } : a)),
      }))
      drafts.current.delete(ref)
      setDraft('')
      setOpenRef(ref)
    } catch (e) {
      setError(errorMessage(e, t('list.errors.replyFailed')))
    } finally {
      setSending(false)
    }
  }

  const report = async (kind: DiscussionTargetKind, id: string) => {
    try {
      await reportItem(kind, id)
      setReported((s) => new Set(s).add(keyOf(kind, id)))
    } catch (e) {
      setNotice(errorMessage(e, t('list.errors.actionFailed')))
    }
  }

  const block = async (kind: DiscussionTargetKind, id: string) => {
    try {
      await blockAuthor(kind, id)
      if (replyTo && kind === 'answer' && replyTo.ref === id) setReplyTo(null)
      onReload()
    } catch (e) {
      setNotice(errorMessage(e, t('list.errors.actionFailed')))
    }
  }

  const moderate = async (kind: DiscussionTargetKind, id: string, visible: boolean) => {
    try {
      await moderateItem(kind, id, visible)
      const flag = { hidden: !visible, hiddenReason: visible ? undefined : 'teacher' }
      setDiscussion((d) => ({
        ...d,
        answers: d.answers.map((a) =>
          kind === 'answer' && a.ref === id
            ? { ...a, ...flag }
            : { ...a, replies: a.replies.map((r) => (kind === 'reply' && r.id === id ? { ...r, ...flag } : r)) },
        ),
      }))
    } catch (e) {
      setNotice(errorMessage(e, t('list.errors.actionFailed')))
    }
  }

  // Optimistic: the heart fills at once, and goes back if the server says no.
  const setLike = (kind: DiscussionTargetKind, id: string, liked: boolean, likes: number) =>
    setDiscussion((d) => ({
      ...d,
      answers: d.answers.map((a) =>
        kind === 'answer' && a.ref === id
          ? { ...a, liked, likes }
          : { ...a, replies: a.replies.map((r) => (kind === 'reply' && r.id === id ? { ...r, liked, likes } : r)) },
      ),
    }))

  const toggleLike = async (kind: DiscussionTargetKind, id: string, liked: boolean, likes: number) => {
    const next = !liked
    setLike(kind, id, next, Math.max(0, likes + (next ? 1 : -1)))
    try {
      const saved = await likeItem(kind, id, next)
      setLike(kind, id, saved.liked, saved.likes)
    } catch (e) {
      setLike(kind, id, liked, likes)
      setNotice(errorMessage(e, t('list.errors.actionFailed')))
    }
  }

  const remove = async (replyId: string) => {
    try {
      await deleteReply(replyId)
      setDiscussion((d) => ({
        ...d,
        answers: d.answers.map((a) => ({ ...a, replies: a.replies.filter((r) => r.id !== replyId) })),
      }))
    } catch (e) {
      setNotice(errorMessage(e, t('list.errors.actionFailed')))
    }
  }

  return {
    discussion,
    openRef,
    toggleThread: (ref: string) => setOpenRef((cur) => (cur === ref ? null : ref)),
    replyTo,
    draft,
    setDraft,
    sending,
    error,
    startReply,
    cancelReply,
    send,
    reported,
    notice,
    toggleLike,
    report,
    block,
    moderate,
    remove,
  }
}

export type DiscussionThreads = ReturnType<typeof useDiscussionThreads>

interface DiscussionListProps {
  threads: DiscussionThreads
  story: any
  activeIndex: string
  /** A tap on a word in an answer or reply. Taps in other learners' answers tell the author. */
  onWordTap: (answer: DiscussionAnswer) => ClickWordHandler
  /** Scrolls this answer into view once, when the list first shows it. */
  scrollToRef?: string | null
}

/** Every answer the viewer may see, each with its thread. The viewer's own answer comes first. */
const DiscussionList: React.FC<DiscussionListProps> = ({ threads, story, activeIndex, onWordTap, scrollToRef }) => {
  const { t } = useTranslation('discussion')
  const zhLang = useChineseLang()
  const { discussion, openRef, reported } = threads
  const scrolled = useRef(false)
  const scrollTarget = useCallback(
    (node: HTMLElement | null) => {
      if (node && !scrolled.current) {
        scrolled.current = true
        node.scrollIntoView({ block: 'start' })
      }
    },
    [],
  )
  if (!discussion) return null
  const canModerate = discussion.canModerate

  const answerReported = (a: DiscussionAnswer) => reported.has(keyOf('answer', a.ref))
  const replyReported = (r: DiscussionReply) => reported.has(keyOf('reply', r.id))

  const answerActions = (a: DiscussionAnswer): MenuAction[] => [
    ...(!a.mine
      ? [
          { label: t('list.actions.reportAnswer'), onSelect: () => threads.report('answer', a.ref) },
          { label: t('list.actions.block'), onSelect: () => threads.block('answer', a.ref) },
        ]
      : []),
    ...(canModerate
      ? [
          {
            label: a.hidden ? t('list.actions.show') : t('list.actions.hide'),
            onSelect: () => threads.moderate('answer', a.ref, !!a.hidden),
          },
        ]
      : []),
  ]

  const replyActions = (r: DiscussionReply): MenuAction[] => [
    ...(r.mine || canModerate ? [{ label: t('list.actions.deleteReply'), onSelect: () => threads.remove(r.id) }] : []),
    ...(!r.mine
      ? [
          { label: t('list.actions.reportReply'), onSelect: () => threads.report('reply', r.id) },
          { label: t('list.actions.block'), onSelect: () => threads.block('reply', r.id) },
        ]
      : []),
    ...(canModerate && !r.mine
      ? [
          {
            label: r.hidden ? t('list.actions.show') : t('list.actions.hide'),
            onSelect: () => threads.moderate('reply', r.id, !!r.hidden),
          },
        ]
      : []),
  ]

  return (
    <div className='dc-list'>
      {threads.notice && (
        <div className='dc-error' role='alert'>
          {threads.notice}
        </div>
      )}
      {discussion.answers.map((a) => {
        if (answerReported(a)) {
          return (
            <div key={a.ref} className='dc-ans dc-gone'>
              {t('list.reported')}
            </div>
          )
        }
        const open = openRef === a.ref
        const count = a.replies.length
        return (
          <article
            key={a.ref}
            id={`dc-answer-${a.ref}`}
            ref={scrollToRef === a.ref ? scrollTarget : undefined}
            className={`dc-ans${open ? ' open' : ''}${a.hidden ? ' is-hidden' : ''}`}
          >
            <div className='dc-who'>
              <Avatar name={a.name} teacher={a.isTeacher} />
              {a.mine ? t('list.yourAnswer') : a.name}
              {a.isTeacher && <TeacherTick />}
              <span className='dc-who-end'>
                {a.hidden && <span className='dc-flag'>{t('list.hidden')}</span>}
                <MoreMenu
                  label={a.mine ? t('list.moreForYourAnswer') : t('list.moreForAnswer', { name: a.name })}
                  actions={answerActions(a)}
                />
              </span>
            </div>
            <div className='dc-text' lang={zhLang}>
              {a.content?.length ? (
                <ZhWords
                  words={a.content}
                  iterator={`DISC-A-${a.ref}-`}
                  story={story}
                  activeIndex={activeIndex}
                  onClickWord={onWordTap(a)}
                  fontSize='16px'
                />
              ) : (
                a.text
              )}
            </div>
            <div className='dc-acts'>
              <LikeButton
                likes={a.likes || 0}
                liked={!!a.liked}
                own={a.mine}
                what={t('list.theirAnswer', { name: a.name })}
                onToggle={() => threads.toggleLike('answer', a.ref, !!a.liked, a.likes || 0)}
              />
              {count > 0 && (
                <button type='button' className='dc-act' aria-expanded={open} onClick={() => threads.toggleThread(a.ref)}>
                  {open ? t('list.hideReplies') : t('replyCount', { count })}
                </button>
              )}
              <button type='button' className='dc-act' onClick={() => threads.startReply(a)}>
                {t('list.reply')}
              </button>
            </div>
            {open && count > 0 && (
              <div className='dc-thread'>
                {a.replies.map((r) =>
                  replyReported(r) ? (
                    <div key={r.id} className='dc-gone'>
                      {t('list.reported')}
                    </div>
                  ) : (
                    <div key={r.id} className={`dc-reply${r.hidden ? ' is-hidden' : ''}`}>
                      <div className='dc-who'>
                        <Avatar name={r.name} teacher={r.isTeacher} />
                        {r.mine ? t('list.you') : r.name}
                        {r.isTeacher && <TeacherTick />}
                        <span className='dc-who-end'>
                          {r.hidden && <span className='dc-flag'>{t('list.hidden')}</span>}
                          {replyTime(r.createdAt) && <span className='dc-when'>{replyTime(r.createdAt)}</span>}
                          <MoreMenu
                            label={r.mine ? t('list.moreForYourReply') : t('list.moreForReply', { name: r.name })}
                            actions={replyActions(r)}
                          />
                        </span>
                      </div>
                      <div className='dc-text' lang={zhLang}>
                        {r.content?.length ? (
                          <ZhWords
                            words={r.content}
                            iterator={`DISC-R-${r.id}-`}
                            story={story}
                            activeIndex={activeIndex}
                            onClickWord={onWordTap(a)}
                            fontSize='15px'
                          />
                        ) : (
                          r.text
                        )}
                      </div>
                      {(!r.mine || r.likes > 0) && (
                        <div className='dc-acts'>
                          <LikeButton
                            likes={r.likes || 0}
                            liked={!!r.liked}
                            own={r.mine}
                            what={t('list.theirReply', { name: r.name })}
                            onToggle={() => threads.toggleLike('reply', r.id, !!r.liked, r.likes || 0)}
                          />
                        </div>
                      )}
                    </div>
                  ),
                )}
              </div>
            )}
          </article>
        )
      })}
    </div>
  )
}

export default DiscussionList
